<!--
    @author bluefirex
-->
<template>
    <div class="acquisit-dev-state-inspector-pdf-debugger default-wrapper">
	    <StateInspectorHeader>
		    <template #default>
			    <h2>Scenario Download</h2>
			    <p class="secondary">
				    Download a TypeScript file to import into AcquisitUI HTML Renderer for debugging purposes.
			    </p>
		    </template>
	    </StateInspectorHeader>
	    
	    <div class="form max-width">
		    <acquisit-info-box v-if="!isTemplateVersionCompatible"
		                       type="error"
		                       :color-container="background">
			   AcquisitUI HTML Renderer only supports templates created with UI Builder.
		    </acquisit-info-box>
		    
		    <div class="pdf-template-selector">
			    <label for="pdf-template">
				   PDF Template:
			    </label>
			    
			    <acquisit-dropdown id="pdf-template"
			                       :color-container="background"
			                       :options="pdfTemplateOptions"
			                       v-model="pdfTemplateVersion" />
		    </div>
		    
		    <div class="actions">
			    <acquisit-button :color-container="background"
			                     label="Download"
			                     icon="acq/download"
			                     icon-position="right"
			                     :disabled="loading || !isTemplateVersionCompatible"
			                     :no-interact="loading || !isTemplateVersionCompatible"
			                     :loading="loading"
			                     @click="downloadTypeScriptFile" />
		    </div>
	    </div>
    </div>
</template>

<script setup lang="ts">
	import { useColor, useColorProps } from '@broker-as/acquisit-ui-vue/helpers'
	import StateInspectorHeader from '@ui/dev/StateInspectorHeader.vue'
	import { computed, ref } from 'vue'
	import { useAPIStore } from '@/stores/api.ts'
	import { useProtocolStore } from '@/stores/protocol.ts'
	import { useGenericStore } from '@/stores/generic.ts'
	import { useBackend } from '@/lib/backend-cors'
	
	const props = defineProps({
		...useColorProps(),
	})
	
	const color = useColor(props)
	const apiStore = useAPIStore()
	const protocolStore = useProtocolStore()
	const backend = useBackend()
	
	// Taken from StateInspector.vue: .acquisit-dev-state-inspector
	const background = computed(() => {
		const scssColor = color.parse('#1d212b')
		return scssColor.darken(10)
	})
	
	const pdfTemplateOptions = computed(() => {
		return {
			'full': {
				label: 'Fullversjon',
				value: 'full'
			},
			'summary': {
				label: 'Summary',
				value: 'summary'
			}
		}
	})
	
	const pdfTemplateVersion = ref<string>('full')
	const loading = ref(false)
	
	const isTemplateVersionCompatible = computed(() => {
		return useGenericStore().is_origin_ui_builder
	})
	
	const getSubmissionPayload = async () => {
		return await protocolStore.createSubmissionPayload()
	}
	
	const getUIPayload = async () => {
		return await backend.getUI(apiStore.token!)
	}
	
	const downloadTypeScriptFile = async () => {
		loading.value = true
		
		try {
			const submission = await getSubmissionPayload()
			const ui = await getUIPayload()
			
			const ts = `export const fileVersion = 1;
			
export const submission = ${JSON.stringify(submission, null, 4)};

export const ui = ${JSON.stringify(ui, null, 4)};

export const type = ${pdfTemplateVersion.value == 'summary' ? '\'summary\'' : '\'full\''};`
			
			const blob = new Blob([ts], { type: 'text/plain' })
			const fileURL = URL.createObjectURL(blob)
			
			const downloadButton = document.createElement('a')
			downloadButton.href = fileURL
			downloadButton.download = `${protocolStore.client?.name}-${pdfTemplateVersion.value}.ts`
			document.body.appendChild(downloadButton)
			downloadButton.click()
			
			setTimeout(() => {
				URL.revokeObjectURL(fileURL)
				document.body.removeChild(downloadButton)
			}, 3000)
		} catch (error) {
			alert('Error creating TypeScript file, check console for more information')
		} finally {
			loading.value = false
		}
	}
</script>

<style lang="scss">
    @use '@/assets/mixins.scss' as m;
    
    .acquisit-dev-state-inspector-pdf-debugger {
	    height: calc(100% - 128px);
	    
	    .max-width {
		    max-width: 540px;
		    margin: auto;
	    }
	    
	    .form {
		    display: flex;
		    flex-direction: column;
		    justify-content: center;
		    gap: 16px;
		    height: 100%;
	    }
    
	    .pdf-template-selector {
		    display: flex;
		    align-items: center;
		    gap: 16px;
		    
		    .acquisit-dropdown {
			    flex: 1;
			    margin: 0;
		    }
	    }
	    
	    .actions {
		    display: flex;
		    justify-content: flex-end;
	    }
    }
</style>